import { type ActiveBoostByPlant } from '@common/types/common';
import {
  type ActiveBoostItemProps,
  type RequirementsItemProps,
  type SelectBoostItem,
} from '@common/types/components';
import { type BoostItemType, type BoostType } from '@common/types/factoryType';
import { type CalcBoostType } from '@common/types/plant';
import { type BuildRequirementsType } from '@common/types/quarryType';
import { type WorkshopRequirements } from '@common/types/workshopType';

import { createBoostTemplate } from '@/common/lib/createBoostTemplate';
import { type PartnerTaskDto } from '@/common/types/query/tasks';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
const createRequirementsList = (
  creationRequirements?: BuildRequirementsType | WorkshopRequirements,
): RequirementsItemProps[] | undefined => {
  if (creationRequirements) {
    const requirementsList: RequirementsItemProps[] = [];
    // const quarries = creationRequirements.quarries;
    const materials = creationRequirements.materials;
    const plants = creationRequirements.plants;

    // if (quarries) {
    //   for (const resource in quarries) {
    //     const resourceName = resource as keyof (typeof quarries);
    //     const value = quarries[resourceName] || 0;
    //     requirementsList.push({ type: 'quarry', resourceName, value });
    //   }
    // }

    if (plants) {
      for (const resource in plants) {
        const resourceName = resource as keyof typeof plants;
        const value = plants[resourceName] || 0;
        requirementsList.push({ type: 'plant', resourceName, value });
      }
    }

    if (materials) {
      for (const resource in materials) {
        const resourceName = resource as keyof typeof materials;
        const value = materials[resourceName] || 0;
        requirementsList.push({ type: 'material', resourceName, value });
      }
    }

    if (creationRequirements.cash) {
      requirementsList.push({
        type: 'cash',
        value: creationRequirements.cash,
      });
    }

    return requirementsList;
  }
};

const selectFreeBoost = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('Free');

  if (boosts) {
    const freeBoostList = boosts.filter(
      ({ boostType }) => boostType === 'Free',
    );
    const freeBoost = freeBoostList[0] as BoostItemType | undefined;

    //backend must send free type boost, but to prevent unexpected crashes, we add a check
    if (freeBoost) {
      let assignSource: BoostItemType = freeBoost;

      if (plantId) {
        const plantInBoost = freeBoost.plants.includes(plantId);

        if (!plantInBoost) {
          assignSource = { ...freeBoost, expiresInSeconds: 0 };
        }
      }

      Object.assign(template, assignSource);
    }
  }

  return template;
};

const selectAdsBoost = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('Advertising');

  if (boosts) {
    const adsBoostList = boosts.filter(
      ({ boostType }) => boostType === 'Advertising',
    );
    const limit = adsBoostList.length;

    if (plantId) {
      const adsBoostForPlant = adsBoostList.find((boost) =>
        boost.plants.includes(plantId),
      );
      let available = 0;
      const availableAdsBoost = adsBoostList.reduce(
        (acc: BoostItemType | undefined, boost: BoostItemType) => {
          if (boost.nextUseInSeconds === 0) {
            ++available;
          }

          if (acc) {
            return (boost.nextUseInSeconds || 0) < (acc.nextUseInSeconds || 0)
              ? boost
              : acc;
          } else {
            return boost;
          }
        },
        undefined,
      ) as BoostItemType;

      const resultBoost = adsBoostForPlant || {
        ...availableAdsBoost,
        expiresInSeconds: 0,
      };
      Object.assign(template, {
        ...resultBoost,
        quantity: { limit, available },
      });

      return template;
    }
  }

  return template;
};

const selectVipBoost = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('VIP');

  if (boosts) {
    const vipBoostList = boosts.filter(({ boostType }) => boostType === 'VIP');
    let assignSource: BoostItemType | undefined;

    if (plantId) {
      assignSource = vipBoostList.reduce(
        (acc: BoostItemType | undefined, boost) => {
          const isBoostForPlant = boost.plants.includes(plantId);

          if (!acc) {
            return isBoostForPlant ? boost : acc;
          }

          return {
            ...acc,
            expiresInSeconds: isBoostForPlant
              ? (acc.expiresInSeconds || 0) + (boost.expiresInSeconds || 0)
              : acc.expiresInSeconds,
          };
        },
        undefined,
      );
    } else {
      assignSource = vipBoostList.reduce(
        (acc: BoostItemType | undefined, boost) => {
          if (!acc) {
            return boost;
          }

          return (boost.expiresInSeconds || 0) > (acc.expiresInSeconds || 0)
            ? boost
            : acc;
        },
        undefined,
      );
    }

    Object.assign(template, assignSource);
  }

  return template;
};

const selectPaidBoost = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('Paid');

  if (boosts) {
    const paidBoostList = boosts.filter(
      ({ boostType }) => boostType === 'Paid',
    );

    let assignSource: BoostItemType | undefined;

    if (plantId) {
      assignSource = paidBoostList.reduce(
        (acc: BoostItemType | undefined, boost) => {
          const isBoostForPlant = boost.plants.includes(plantId);

          if (!acc) {
            return isBoostForPlant ? boost : acc;
          }

          return {
            ...acc,
            expiresInSeconds: isBoostForPlant
              ? (acc.expiresInSeconds || 0) + (boost.expiresInSeconds || 0)
              : acc.expiresInSeconds,
          };
        },
        undefined,
      );

      Object.assign(template, assignSource);
    }
  }

  return template;
};

const selectBoostX5 = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('BoostForCrypto', 5);

  if (boosts) {
    const boostList = boosts.filter(
      ({ boostType, effectMultiplier }) =>
        boostType === 'BoostForCrypto' && effectMultiplier === 5,
    );

    let assignSource: BoostItemType | undefined;

    if (plantId) {
      assignSource = boostList.reduce(
        (acc: BoostItemType | undefined, boost) => {
          const isBoostForPlant = boost.plants.includes(plantId);

          if (!acc) {
            return isBoostForPlant ? boost : acc;
          }

          return {
            ...acc,
            expiresInSeconds: isBoostForPlant
              ? (acc.expiresInSeconds || 0) + (boost.expiresInSeconds || 0)
              : acc.expiresInSeconds,
          };
        },
        undefined,
      );

      Object.assign(template, assignSource);
    }
  }

  return template;
};
const selectBoostX10 = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): SelectBoostItem => {
  const template = createBoostTemplate('BoostForCrypto', 10);

  if (boosts) {
    const boostList = boosts.filter(
      ({ boostType, effectMultiplier }) =>
        boostType === 'BoostForCrypto' && effectMultiplier === 10,
    );

    let assignSource: BoostItemType | undefined;

    if (plantId) {
      assignSource = boostList.reduce(
        (acc: BoostItemType | undefined, boost) => {
          const isBoostForPlant = boost.plants.includes(plantId);

          if (!acc) {
            return isBoostForPlant ? boost : acc;
          }

          return {
            ...acc,
            expiresInSeconds: isBoostForPlant
              ? (acc.expiresInSeconds || 0) + (boost.expiresInSeconds || 0)
              : acc.expiresInSeconds,
          };
        },
        undefined,
      );
      Object.assign(template, assignSource);
    }
  }

  return template;
};

const calculateTotalBoost = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string | undefined,
): number | undefined => {
  let result: number | undefined;
  const boostTypes = new Set<BoostType>();

  if (boosts) {
    boosts.forEach((boost) => {
      const isDublicateVip =
        boost.boostType === 'VIP' && boostTypes.has(boost.boostType);
      const isDublicatePaid =
        boost.boostType === 'Paid' && boostTypes.has(boost.boostType);
      const boostIsActive = (boost.expiresInSeconds || 0) > 0;

      if (
        plantId &&
        boost.plants.includes(plantId) &&
        boostIsActive &&
        !(isDublicateVip || isDublicatePaid)
      ) {
        result = result
          ? result + boost.effectMultiplier
          : boost.effectMultiplier;
        boostTypes.add(boost.boostType);
      }
    });
  }

  return result;
};

const generateActiveBoostList = (
  boosts: BoostItemType[] | null | undefined,
): ActiveBoostItemProps[] => {
  const mapResult = new Map<string, ActiveBoostItemProps>([]);
  const boostTypes = new Map<string, BoostType[]>();

  if (boosts) {
    boosts.forEach(
      ({ plants, expiresInSeconds, effectMultiplier, boostType }) => {
        plants.forEach((plantId) => {
          if (mapResult.has(plantId)) {
            const item = mapResult.get(plantId) as ActiveBoostItemProps;

            if (
              (expiresInSeconds || 0) > item.expiresInSeconds &&
              (expiresInSeconds || 0) > 0
            ) {
              item.expiresInSeconds = expiresInSeconds || 0;
            }

            const plantData = boostTypes.get(plantId);
            const isDublicateBoostType =
              plantData && plantData.includes(boostType);
            const isDublicateVip = boostType === 'VIP' && isDublicateBoostType;
            const isDublicatePaid =
              boostType === 'Paid' && isDublicateBoostType;

            if (
              (expiresInSeconds || 0) > 0 &&
              !(isDublicateVip || isDublicatePaid)
            ) {
              item.effectMultiplier += effectMultiplier;
            }
          } else {
            if (expiresInSeconds) {
              mapResult.set(plantId, {
                id: plantId,
                expiresInSeconds,
                effectMultiplier,
                plantId,
                boostType,
              });
            }
          }

          if (boostTypes.has(plantId)) {
            const palntData = boostTypes.get(plantId) as BoostType[];
            palntData.push(boostType);
          } else {
            boostTypes.set(plantId, [boostType]);
          }
        });
      },
    );
  }

  return [...mapResult.values()];
};

const getMaxExpiresTimeByPlant = (
  boosts: BoostItemType[] | null | undefined,
  plantId: string,
): { expiresTime: number; duration: number } => {
  let expiresTime = 0;
  let duration = 0;

  boosts?.forEach((boost) => {
    if (
      boost.plants.includes(plantId) &&
      (boost?.expiresInSeconds || 0) > expiresTime
    ) {
      expiresTime = boost.expiresInSeconds || 0;
      duration = boost.duration || 0;
    }
  });

  return { expiresTime, duration };
};

const mapBoostForCalcMining = (
  boosts: BoostItemType[] | undefined | null,
  plantId: string | undefined,
): CalcBoostType[] | undefined => {
  if (boosts && plantId) {
    return boosts
      .filter(
        (boost) =>
          boost.plants.includes(plantId) && boost.startedAt && boost.endDate,
      )
      .map(({ effectMultiplier, startedAt, endDate }) => ({
        effectMultiplier,
        startedAt: new Date(Date.parse(startedAt as string)),
        endDate: new Date(Date.parse(endDate as string)),
      }));
  }
};

const getActiveBoost = (
  boosts: BoostItemType[] | undefined | null,
): ActiveBoostByPlant => {
  const result: ActiveBoostByPlant = {};

  if (boosts) {
    boosts.forEach((boost) => {
      boost.plants.forEach((id) => {
        if (id in result) {
          result[id].push({
            multiplier: boost.effectMultiplier,
            boostType: boost.boostType,
            duration: boost.duration,
          });
        } else {
          result[id] = [
            {
              multiplier: boost.effectMultiplier,
              boostType: boost.boostType,
              duration: boost.duration,
            },
          ];
        }
      });
    });
  }

  return result;
};

const checkIfBoostTimersEnd = (
  boosts: BoostItemType[] | undefined | null,
): boolean => {
  if (boosts) {
    return boosts.every(
      (boost) =>
        (boost.expiresInSeconds || 0) === 0 &&
        (boost.nextUseInSeconds || 0) === 0,
    );
  } else {
    return true;
  }
};

const checkIfFreeBoostAvailable = (
  boosts: BoostItemType[] | undefined | null,
): boolean => {
  if (boosts) {
    return boosts.some(
      ({ boostType, nextUseInSeconds }) =>
        boostType === 'Free' && nextUseInSeconds === 0,
    );
  } else {
    return false;
  }
};

const checkArenaDiscountTimer = (
  arenaOffers: ArenaOffersDto[] | undefined | null,
): boolean => {
  if (arenaOffers && arenaOffers.length) {
    return arenaOffers.some((el) => el.discountTimeLeftInSeconds > 0);
  } else return false;
};

const expiriesArenaDiscountTimer = (
  arenaOffers: ArenaOffersDto[] | undefined | null,
): boolean => {
  if (arenaOffers && arenaOffers.length) {
    return arenaOffers.some(
      (el) =>
        el.discountTimeLeftInSeconds <= 0 &&
        el.cost.has_discount &&
        el.name !== 'superStart',
    );
  } else return false;
};

const arenaShownPopup = (
  arenaOffers: ArenaOffersDto[] | undefined | null,
): boolean => {
  if (arenaOffers && arenaOffers.length) {
    return arenaOffers.some((el) => !el.popupShown && el.cost.has_discount);
  } else return false;
};

const checkTimeLeftArenaOffer = (
  arenaOffers: ArenaOffersDto[] | undefined | null,
): boolean => {
  if (arenaOffers && arenaOffers.length) {
    return arenaOffers.some((el) => el.timeLeftInSeconds <= 7200);
  } else return false;
};

const checkPartnerTaskTimer = (
  task: PartnerTaskDto[] | undefined | null,
): boolean => {
  if (task && task.length) {
    return task.some((el) => el.delayTimer && el.delayTimer > 0);
  } else return false;
};

export {
  arenaShownPopup,
  calculateTotalBoost,
  checkArenaDiscountTimer,
  checkIfBoostTimersEnd,
  checkIfFreeBoostAvailable,
  checkPartnerTaskTimer,
  checkTimeLeftArenaOffer,
  createRequirementsList,
  expiriesArenaDiscountTimer,
  generateActiveBoostList,
  getActiveBoost,
  getMaxExpiresTimeByPlant,
  mapBoostForCalcMining,
  selectAdsBoost,
  selectBoostX5,
  selectBoostX10,
  selectFreeBoost,
  selectPaidBoost,
  selectVipBoost,
};
